import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CimaCore, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/angular';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { LineDataPlan, PlanEligibility } from 'store/line/models';
import { AddonCollections } from 'store/addons';
import { SwitchUtil } from '../../../shared/services/switch-util';
import { Account, User } from 'store/user/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';
import { BreadcrumbLabel } from 'my-account/constants';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';

@Component({
    selector: 'plan-switch-review',
    styleUrls: ['./review.scss'],
    templateUrl: './review.html',
    standalone: false
})
export class PlanSwitchReviewComponent implements OnInit, OnDestroy {
    public planSwitchReviewPageData: CmsPlanSwitchReviewData;
    public selectedPlanData: CmsDataPlanDetails;
    public dataPlanCollection: CmsLineDataPlanCollection;
    public isUnlimited: boolean;
    public features: CmsDetailPlanFeature[] = [];
    public pageData: CmsPlanSwitchReview;
    public iconOption: MediaImageOptions;
    public header: string;
    public description: string;
    public lineSummary: LineSummary;
    public summary: AccountSummary;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public user: User;
    public pageHeader: string;
    public isBusinessUser: boolean;
    public enablePlanSevenResi: boolean;
    public enablePlanSevenCBM: boolean;
    public legacyPromoDetails: targetPlanSwitchDetails;
    public isIcopPlanSwitch: boolean;
    public isBillPreviewEnabled: boolean;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private state: StateService;
    private switchUtil: SwitchUtil;
    private cimaCore: CimaCore;
    private config: IXMOptions;

    constructor(xmStore: XmStore, state: StateService, switchUtil: SwitchUtil, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, state, switchUtil, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.isBillPreviewEnabled = (this.config.ENABLE_BILL_PREVIEW && !this.cimaCore.isBusinessUser) || (this.config.ENABLE_CBM_BILL_PREVIEW && this.cimaCore.isBusinessUser);

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.pageData = result.planSwitchReview;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.enablePlanSevenResi = !this.isBusinessUser && this.user.account.isSegmentFive;
            this.enablePlanSevenCBM = this.isBusinessUser && this.user.account.isSegmentFive;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanCollection = result;
            const planCode: string = decodeURIComponent(this.state.params.plan_code);
            this.selectedPlanData = this.dataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(planCode));
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (summary: AccountSummary) => {
            this.summary = summary;
            this.lineSummary = this.summary.getLineByKey(this.state.params.line_id);
        }));

        // TODO: Use this peek to map icop promo content fot seg 4 and below users.
        if (this.isIcopPlanSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (legacyPromoDetails: targetPlanSwitchDetails) => {
                this.legacyPromoDetails = legacyPromoDetails;
            }));
        }

        if ((!this.user.account.isSegmentFive) || (!this.enablePlanSevenResi && !this.enablePlanSevenCBM)) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
                filters: { lineKey: this.state.params.line_id }
            }), (planEligibility: PlanEligibility) => {
                this.planEligibility = planEligibility;
                const planCode: string = decodeURIComponent(this.state.params.plan_code);
                this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === planCode);
                this.isUnlimited = this.eligiblePlanDetails.isUnlimited;
                if (!this.isUnlimited && this.summary.level5Customer){
                    this.planSwitchReviewPageData = this.pageData.byTheGigPlanSixSegFive;
                    this.selectedPlanData.pricePerUnit = this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
                    this.pageHeader = this.selectedPlanData.planName;
    
                } else if (!this.isUnlimited && !this.summary.level5Customer){
                    this.planSwitchReviewPageData = this.pageData.unlimited;
                    this.selectedPlanData.pricePerUnit = this.eligiblePlanDetails.postSwitch.planGroupCost.toString();
                    this.pageHeader = this.selectedPlanData.planName;
    
                } else {
                    this.planSwitchReviewPageData = this.pageData.byTheGig;
                    this.selectedPlanData.pricePerUnit = this.eligiblePlanDetails.postSwitch.lineCost.toString();
                    this.pageHeader = this.selectedPlanData.planType;
                }  
                 
                this.setData();
            }));
        } else {
            this.pageHeader = BreadcrumbLabel.BY_THE_GIG;
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public navigateTo(isProgress: boolean): void {
        if (isProgress) {
            if (this.isBillPreviewEnabled) {
                this.state.go('account.activity.details.plan-switch.review-order', { line_id: this.state.params.line_id, plan_code: this.state.params.plan_code,
                    backButtonPath: 'account.activity.details.plan-switch.review' });
            } else {
                this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.state.params.line_id, plan_code: this.state.params.plan_code });
            }
        } else {
            this.state.go('account.activity.details', { line_id: this.state.params.line_id });
        }
    }

    private setData(): void {
        const { isSegmentThree, isSegmentFour, isSegmentFive }: Account = this.user.account;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AddonCollections>(AddonCollections, {
            filters: { lineId: this.state.params.line_id }
        }), (addons: AddonCollections) => {
            const planCode: string = decodeURIComponent(this.state.params.plan_code);
            const eligiblePlanDetails: LineDataPlan = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === planCode);
            const unlimitedLinesCount: number = (this.isUnlimited ?
                eligiblePlanDetails && eligiblePlanDetails.postSwitch && eligiblePlanDetails.postSwitch.numberOfLines :
                eligiblePlanDetails && eligiblePlanDetails.postSwitch && eligiblePlanDetails.postSwitch.remainingUnlimitedLines) || 0;

            let switchFeatures: CmsDetailPlanSwitchFeature;

            if (this.isUnlimited && (isSegmentThree || isSegmentFour)) {
                switchFeatures = this.selectedPlanData.featuresForLevel3or4[0];
                this.planSwitchReviewPageData.description = this.planSwitchReviewPageData.descriptionForLevel3or4;
            } else if (!this.isUnlimited && isSegmentFive && this.summary.level5Customer) {
                switchFeatures = this.selectedPlanData.planSixSegFiveFeatures[0];
            } else if (!this.isUnlimited && !isSegmentFive && this.isBusinessUser) {
                switchFeatures = this.selectedPlanData.CBMFeaturesForOtherSegements[0];
            } else if (addons.hasHdPass && this.selectedPlanData.hdPassFeatures) {
                switchFeatures = this.selectedPlanData.hdPassFeatures.find(
                    (data: CmsDetailPlanSwitchFeature) => data.unlimitedLinesCount.includes(unlimitedLinesCount));
            } else {
                switchFeatures = this.selectedPlanData.noHdPassFeatures.find(
                    (data: CmsDetailPlanSwitchFeature) => data.unlimitedLinesCount.includes(unlimitedLinesCount));
            }

            switchFeatures.features.forEach((feature: CmsDetailPlanFeature) => {
                this.features.push({ ...feature, header: this.switchUtil.switchSubstitution((isSegmentFour && feature.seg4Header) ? feature.seg4Header : feature.header, this.lineSummary.displayName, eligiblePlanDetails) });
            });
        }));
    }
}
